'use client';

import { motion } from 'framer-motion';
import { Cloud, Euro, Info, Leaf } from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ASSUMPTION_KEYS, DEFAULT_ASSUMPTIONS } from '@/features/food-waste/constants';
import { formatAssumption, formatEur, formatMass } from '@/features/food-waste/utils/format';
import type { PlatformSavings } from '@/features/landing/utils/platform-savings';
import { cn } from '@/lib/utils';

interface HeroSavingsStatsProps {
  stats: PlatformSavings;
}

/**
 * The three estimated savings under the hero video: ingredient cost, food not
 * cooked and CO2e, per 100 employees per year, with the in-app calculator's
 * formatters. "How we estimate" opens the four default assumptions. Rendered
 * only with a positive estimate; the page passes null otherwise.
 */
export function HeroSavingsStats({ stats }: HeroSavingsStatsProps) {
  const t = useTranslations('landing.hero.stats');
  const tFoodWaste = useTranslations('foodWaste');
  const format = useFormatter();
  const food = formatMass(format.number, stats.kg);
  const co2 = formatMass(format.number, stats.co2Kg);

  const items = [
    {
      key: 'eur',
      icon: Euro,
      label: t('eur'),
      value: formatEur(format.number, stats.eur),
    },
    {
      key: 'kg',
      icon: Leaf,
      label: t('kg'),
      value: tFoodWaste(food.unit === 't' ? 'results.units.foodT' : 'results.units.foodKg', {
        value: food.value,
      }),
    },
    {
      key: 'co2',
      icon: Cloud,
      label: t('co2'),
      value: tFoodWaste(co2.unit === 't' ? 'results.units.co2T' : 'results.units.co2Kg', {
        value: co2.value,
      }),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="mt-4 flex flex-col items-center gap-3"
      data-testid="landing-savings-stats"
    >
      <p className="text-center text-sm text-muted-foreground">{t('title')}</p>

      {/* Three equal columns on a phone (no icons, smaller values), a divided row from `sm`.
          One group div per item (dl > div > dt + dd); the divider is its left border. */}
      <dl className="grid w-full grid-cols-3 gap-2 sm:flex sm:w-auto sm:items-start sm:justify-center sm:gap-6">
        {items.map((item, index) => (
          <div
            key={item.key}
            className={cn(
              // Value above its label; the DOM keeps dt before dd.
              'flex min-w-0 flex-col-reverse text-center',
              index > 0 && 'sm:border-l sm:border-border/50 sm:pl-6',
            )}
          >
            <dt className="text-xs text-muted-foreground sm:text-sm">{item.label}</dt>
            <dd className="mb-1 flex items-center justify-center gap-2">
              <item.icon
                className="hidden h-4 w-4 shrink-0 text-primary sm:block"
                aria-hidden="true"
              />
              <span
                className="whitespace-nowrap text-base font-bold tabular-nums text-foreground sm:text-2xl"
                data-testid={`landing-savings-${item.key}-value`}
              >
                {item.value}
              </span>
            </dd>
          </div>
        ))}
      </dl>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="min-h-[44px] text-muted-foreground"
            data-testid="landing-savings-assumptions-trigger"
          >
            <Info aria-hidden="true" />
            {t('assumptionsTrigger')}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 space-y-3 text-sm"
          data-testid="landing-savings-assumptions"
        >
          <p>{t('method')}</p>
          <dl className="space-y-1">
            {ASSUMPTION_KEYS.map((key) => (
              <div key={key} className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">{tFoodWaste(`assumptions.${key}.label`)}</dt>
                <dd
                  className="shrink-0 tabular-nums"
                  data-testid={`landing-savings-assumption-${key}`}
                >
                  {tFoodWaste('assumptions.valueWithUnit', {
                    value: formatAssumption(format.number, key, DEFAULT_ASSUMPTIONS[key]),
                    unit: tFoodWaste(`assumptions.${key}.unit`),
                  })}
                </dd>
              </div>
            ))}
          </dl>
        </PopoverContent>
      </Popover>
    </motion.div>
  );
}
